import { EmbeddedPage } from '@tinct/core-api-models';

/**
 * Whether "Generate all" will (re)generate this page — i.e. whether it will
 * consume a landing-page generation credit.
 *
 * Mirrors the backend's skip rules in
 * `CampaignAudienceService#generateAllPages`:
 * - excluded pages are never generated, however stale they are;
 * - a page that already has content is only regenerated once it has gone stale
 *   (`needs_regeneration`), which is how an edit to a live campaign schedules
 *   work without downgrading the page's status;
 * - a page already generating is skipped, since its job is still running.
 *
 * Requires `status` and `outdatedStatus` in the page projection — a projection
 * missing `outdatedStatus` reports every live page as up to date.
 */
export function pageNeedsGeneration(page: EmbeddedPage): boolean {
  const isStale = page.outdatedStatus === 'needs_regeneration';

  switch (page.status) {
    // Never generated (or the last attempt failed): always pending work.
    case 'draft':
    case 'waiting':
    case 'queued':
    case 'error':
      return true;

    // Already has content — only regenerated once it goes stale.
    case 'published':
    case 'unpublished':
    case 'ready':
      return isStale;

    // A run is already in flight, and excluded pages are skipped outright.
    case 'generating':
    case 'excluded':
    case 'excluded_conflict':
      return false;

    default:
      return false;
  }
}
